import React, { useCallback } from 'react';
import { EUserType, IUser, TInternalUserRoles } from 'services/BackendApi';
import { SalesUserSelect } from './SalesUserSelect';
import { MonthRangePicker } from 'ui/MonthPicker/MonthRangePicker';
import classNames from 'classnames';
import { useResponsive } from '../hooks/useResponsive';
import { useInternalUsers } from 'services/BackendApi/queries/useSalesUsers';

interface IDashboardHeaderProps {
  isAdmin: boolean;
  currentUser: IUser;
  salesUserId: string | undefined;
  dateFrom: string;
  dateTo: string;
  onSalesUserIdChange: (salesUserId: string | undefined) => void;
  onDateFromChange: (dateFrom: string) => void;
  onDateToChange: (dateTo: string) => void;
}

export const DashboardHeader: React.FC<IDashboardHeaderProps> = ({
  isAdmin,
  salesUserId,
  onSalesUserIdChange,
  onDateFromChange,
  onDateToChange,
  currentUser,
  dateFrom,
  dateTo,
}) => {
  const { isMobile, isTablet, isDesktop, isDesktopHD } = useResponsive();
  const filter = {
    userRole: EUserType.SALES as TInternalUserRoles,
    search: null,
    email: null,
    countryCode: null,
    status: null,
  };
  const { data: salesUsers, isLoading: isLoadingSalesUsers } = useInternalUsers(filter);
  const selectedSalesUser = salesUsers?.data?.data?.find(user => user.uuid === salesUserId);

  const handleMonthRangeChange = useCallback((monthFrom: string, monthTo: string) => {
    onDateFromChange(monthFrom);
    onDateToChange(monthTo);
  }, []);

  const containerClassName = classNames('crm-dashboard-header flex', {
    'flex-col gap-[10px]': isMobile || isTablet,
    'flex-row items-center justify-between': isDesktop || isDesktopHD,
  });

  const contentClassName = classNames('flex items-start', {
    'gap-[12px] justify-between': isMobile,
    'gap-[20px]': isTablet || isDesktop || isDesktopHD,
  });

  return (
    <div className={containerClassName}>
      <p className="dashboard-title font-noe-display m-0">
        <span className="primary-title font-noe-display text-4xl leading-46px text-black font-normal m-0">
          Sales Dashboard
        </span>
        {isAdmin && (
          <span className="secondary-title font-noe-display text-26px leading-33px text-black font-normal m-0 ml-5px">
            &nbsp;-{' '}
            {selectedSalesUser ? `${selectedSalesUser.firstName} ${selectedSalesUser.lastName}` : 'All Sales Agents'}
          </span>
        )}
      </p>

      <div className={contentClassName}>
        {salesUsers && (
          <SalesUserSelect
            isAdmin={isAdmin}
            salesUserId={salesUserId}
            onSalesUserIdChange={onSalesUserIdChange}
            salesUsers={salesUsers}
            isLoadingSalesUsers={isLoadingSalesUsers}
          />
        )}
        <MonthRangePicker
          monthFrom={dateFrom}
          monthTo={dateTo}
          onMonthRangeChange={handleMonthRangeChange}
          eachMonthPickerWidth="120px"
          outputAsFullDate
        />
      </div>
    </div>
  );
};
